/**
 * Beta Signup Helpers
 * Utilities for managing beta user signups and waitlist
 */

import { supabase } from '@/integrations/supabase/client';
import { isBetaEnvironment, BETA_CONFIG } from './betaConfig';

export interface BetaSignupResult {
  allowed: boolean;
  reason?: 'limit_reached' | 'error';
  message?: string;
  waitlistId?: string;
}

/**
 * Check if beta signup is allowed (under BETA_USER_LIMIT)
 */
export async function canSignupForBeta(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('can_create_beta_user');

    if (error) {
      console.error('Error checking beta limit:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Exception checking beta limit:', error);
    return false;
  }
}

/**
 * Add user to beta waitlist when limit is reached
 */
export async function addToBetaWaitlist(
  email: string,
  userAgent?: string
): Promise<{ success: boolean; id?: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('beta_waitlist')
      .insert({
        email,
        source: 'beta_subdomain',
        user_agent: userAgent || navigator.userAgent,
        requested_at: new Date().toISOString(),
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error adding to waitlist:', error);
      return { success: false, error: error.message };
    }

    return { success: true, id: data.id };
  } catch (error: any) {
    console.error('Exception adding to waitlist:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Track successful beta signup
 */
export async function trackBetaSignup(
  userId: string,
  email: string,
  userAgent?: string
): Promise<void> {
  try {
    await supabase
      .from('beta_signups')
      .insert({
        user_id: userId,
        email,
        source: 'beta_subdomain',
        user_agent: userAgent || navigator.userAgent,
        status: 'accepted',
      });
  } catch (error) {
    // Don't fail signup if tracking fails
    console.error('Error tracking beta signup:', error);
  }
}

/**
 * Get beta metadata for user signup
 */
export function getBetaMetadata() {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days

  return {
    is_beta_user: true,
    beta_activated_at: now.toISOString(),
    beta_expires_at: expiresAt.toISOString(),
  };
}

/**
 * Check if user should signup as beta user
 * Returns metadata to include in signup if beta, or null if regular signup
 */
export async function prepareBetaSignup(
  email: string
): Promise<BetaSignupResult> {
  // Only apply beta logic if on beta subdomain
  if (!isBetaEnvironment()) {
    return { allowed: true };
  }

  // Check if beta limit reached
  const canSignup = await canSignupForBeta();

  if (!canSignup) {
    // Add to waitlist
    const waitlistResult = await addToBetaWaitlist(email);

    return {
      allowed: false,
      reason: 'limit_reached',
      message: `Beta is currently full (${BETA_CONFIG.BETA_USER_LIMIT}/${BETA_CONFIG.BETA_USER_LIMIT} users). You've been added to our waitlist!`,
      waitlistId: waitlistResult.id,
    };
  }

  return { allowed: true };
}

/**
 * Get beta analytics (for admin dashboard)
 */
export async function getBetaAnalytics() {
  try {
    const { data, error } = await supabase
      .from('beta_analytics')
      .select('*')
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching beta analytics:', error);
    return null;
  }
}

/**
 * Get beta waitlist (for admin dashboard)
 */
export async function getBetaWaitlist() {
  try {
    const { data, error } = await supabase
      .from('beta_waitlist')
      .select('*')
      .order('requested_at', { ascending: false });

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching beta waitlist:', error);
    return [];
  }
}

/**
 * Mark waitlist user as notified
 */
export async function markWaitlistNotified(waitlistId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('beta_waitlist')
      .update({
        notified: true,
        notified_at: new Date().toISOString(),
      })
      .eq('id', waitlistId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error marking waitlist as notified:', error);
    return false;
  }
}

/**
 * Get user's client count (for beta limit tracking)
 */
export async function getUserClientCount(userId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('applicant_profiles')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_active', true);

    if (error) throw error;

    return count || 0;
  } catch (error) {
    console.error('Error getting user client count:', error);
    return 0;
  }
}

/**
 * Get user's beta status information
 */
export async function getBetaStatus(userId: string) {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error || !user) {
      return null;
    }

    const metadata = user.user_metadata || {};
    const isBetaUser = metadata.is_beta_user === true;
    const betaExpiresAt = metadata.beta_expires_at;
    const betaActivatedAt = metadata.beta_activated_at;

    if (!isBetaUser || !betaExpiresAt) {
      return null;
    }

    const now = new Date();
    const expiresDate = new Date(betaExpiresAt);
    const daysRemaining = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const isExpired = daysRemaining <= 0;

    // Get client count
    const clientCount = await getUserClientCount(userId);

    return {
      isBetaUser,
      betaExpiresAt,
      betaActivatedAt,
      daysRemaining,
      isExpired,
      clientCount,
      clientLimit: 10,
    };
  } catch (error) {
    console.error('Error getting beta status:', error);
    return null;
  }
}
